<template>
    <features-template-base
    :customBreadcrumbs="customBreadcrumbs"
    :customHeaderTabs="customHeaderTabs"
    :showLoader="showFullViewLoader"
    :showOverlayLoader="showOverlayLoader"
    :tokenTitleAppend="tokenTitleAppend"
    :tokenTitleFull="tokenTitleFull"
    :tokenParams="tokenParams"
    :tokenCustomUseParams="tokenCustomUseParams"
    :applyErrorState="applyErrorState"
    :showSearchSlot="showSearchSlot"
    :showTopRightAreaSlot="showTopRightAreaSlot"
    @scrollValueUpdated="scrollValue = $event"
    @challengeSuccessHandler="emit('challengeSuccessHandler', $event)"
    @challengeErrorHandler="emit('challengeErrorHandler', $event)"
    @customTabClicked="emit('customTabClicked', $event)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Mobile top right button slot -->
        <template v-if="showTopRightButtonMobileSlot" #top-right-button-mobile-slot><slot name="top-right-button-mobile-slot"/></template>
        <!-- Slot for elements position on top area of header in web, and on top of content in mobile -->
		<template v-if="showTopRightAreaSlot" #accounts-card-slot-area><slot name="accounts-card-slot-area"/></template>
        <!-- Slot for search in navigation-header -->
        <template v-if="showSearchSlot" #top-search-area-slot><slot name="top-search-area-slot"/></template>
        <!--  -->
        <template v-if="showTabsExtraSlot" #tabs-slot-extra-content><slot name="tabs-slot-extra-content"/></template>
        <!-- Full wrapper element -->
        <div
        v-if="showTopFullSlot && !showFullViewLoader"
        ref="topFullWrapper"
        class="top-full-wrapper"
        :class="{'apply-margin-bottom': showLeftColumnSlot || showRightColumnSlot}">
            <slot name="top-full-wrapper"></slot>
        </div>
        <!-- Two Columns wrapper -->
        <div
        v-if="!showFullViewLoader"
        class="two-column-wrapper"
        :class="{'mobile': getMobileAppState, 'menu-collapsed': menuCollapsed, 'full-height': applyFullHeightToRightColumn}">
            <div v-if="showLeftColumnSlot" ref="leftColumnRef" class="left-column-wrapper" :style="{'top': leftColumnTopPosition}">
                <slot name="left-column"></slot>
            </div>
            <div v-if="showRightColumnSlot" class="right-column-wrapper">
                <slot name="right-column"></slot>
            </div>
        </div>
        <!-- Empty-State-->
        <template #empty-state-slot><slot name="empty-state-slot"></slot></template>
    </features-template-base>
</template>

<script setup>
import FeaturesTemplateBase from '@/components/wrappers/featuresTemplates/Base'
/* Vue */
import { ref, computed, useSlots } from 'vue'
/* Assets */
import { validateIfParentExists } from '@/assets/scripts/htmlManipulators.js'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked', 'customTabClicked', 'challengeSuccessHandler', 'challengeErrorHandler'])

/* Props
@menuCollapsed {Boolean}
@customBreadcrumbs {Object} - if our breadcrumbs are not direct from menu construction based on route, use this object
@customHeaderTabs {Array} - array with custom tabs
@showTopFullSlot {Boolean}
@showTopRightAreaSlot {Boolean}
@showSearchSlot {Boolean}
@showLeftColumnSlot {Boolean}
@showRightColumnSlot {Boolean}
@showOverlayLoader {Boolean} - prop to apply overlay loader
@showFullViewLoader {Boolean} - prop to show loader
@applyFullHeightToRightColumn {Boolean} - prop to apply correct styles for this behaviour
@applyErrorState {Boolean} - prop to apply Error State
@errorStateMessage {String} - message to display on errorState
@tokenTitleAppend {String} - string to appentd at token modal title
@tokenTitleFull {String} - string to use as token modal title
@tokenParams {Object} - some times token submit will have additional params to send with token, when so, send it in this prop
@tokenCustomUseParams {Object} - when we want to have this modal but with apis diferent than the generic ones we send this object with:
    - messageToDisplay {String}
    - sendTokenAction {string} - 'moduleName/actionName'
    - resendTokenAction {string} - 'moduleName/actionName'
    - payload {Object} info that we want to send along with the response
    - resendPayload {Object} with info needed to resent token
    - forceResendChannelString {String} - ['email', 'sms'] - prop to ignore session token channel and use custom one
*/
const props = defineProps({
    menuCollapsed: Boolean,
    customBreadcrumbs: Object,
    customHeaderTabs: Array,
    /* Slots display controller */
    showSearchSlot: Boolean,
    showTopRightAreaSlot: Boolean,
    showTopFullSlot: Boolean,
    showLeftColumnSlot: Boolean,
    showRightColumnSlot: Boolean,
    applyFullHeightToRightColumn: Boolean,
    /* Loader Props */
    showOverlayLoader: Boolean,
    showFullViewLoader: Boolean,
    /* ErrorState Props */
    applyErrorState: Boolean,
    errorStateMessage: String,
    /* Token related Props */
    tokenTitleAppend: String,
    tokenTitleFull: String,
    tokenParams: Object,
    tokenCustomUseParams: Object
})

const { getMobileAppState } = mapGetters()

const scrollValue = ref(0)
const topFullWrapper = ref('')
const leftColumnRef = ref('')

const leftColumnTopPosition = computed(() => {
    const preventCalcPosition = getMobileAppState.value || !useSlots()['left-column']
    const scrollValueTemp = scrollValue.value
    let hiddenAreaVerticalSize = -24

    if (preventCalcPosition) return

    const scrollElementParent = validateIfParentExists(leftColumnRef.value, 'feature-content-container')
    const topFullWrapperHeight = props.showTopFullSlot ? topFullWrapper.value.scrollHeight + 24 : 0
    const leftElementHeight = leftColumnRef.value?.scrollHeight + topFullWrapperHeight

    if (!scrollElementParent) return

    if (leftElementHeight > scrollElementParent.clientHeight) {
        hiddenAreaVerticalSize = leftElementHeight - scrollElementParent.clientHeight

        if (scrollValueTemp < hiddenAreaVerticalSize) return
    }

    const topPosition = scrollValueTemp - hiddenAreaVerticalSize - 24 // padding bottom

    return `${topPosition}px`
})
/*
* For overlay header on scroll,
* we need to make sure that our slots are not being populated, and if not, remove them with a v-if
*/
const showTopRightButtonMobileSlot = computed(() => {
    return useSlots()['top-right-button-mobile-slot']
})

const showTabsExtraSlot = computed(() => {
    return useSlots()['tabs-slot-extra-content']
})
</script>

<style lang="scss" scoped>
.top-full-wrapper {
    width: 100%;
    max-width: 1168px;
    margin: 0 auto;
    height: auto;

    &.apply-margin-bottom {
        margin-bottom: 24px;
    }
}

.two-column-wrapper {
    width: 100%;
    max-width: 1168px;
    margin: 0 auto;

    &:not(.mobile) {
        padding-left: 392px; /* left-side + 16px between them */
        position: relative;

        &.full-height {
            height: 100%;

            .right-column-wrapper {
                height: 100%;

                :deep(.right-side-content) {
                    height: 100%;
                }
            }
        }

        &.menu-collapsed {
            padding-left: 472px;

            .left-column-wrapper {
                width: 456px;
            }
        }

        .left-column-wrapper {
            width: 376px;
            padding-bottom: 16px;

            position: absolute;
            left: 0;
            top: 0;
        }

        .right-column-wrapper {
            @include cardGeneral1;
            padding: 0;

            background-color: transparent;
        }
    }

    &.mobile {
        padding-left: 0;

        .left-column-wrapper {
            position: relative;
            margin-bottom: 16px;
        }

        .right-column-wrapper {
            @include cardGeneral1;
            padding: 0;

            background-color: transparent;
        }
    }
}
</style>
